import { computePosition, flip, offset, shift } from "@floating-ui/dom";
import type { Editor, Range } from "@tiptap/core";
import type { SuggestionProps } from "@tiptap/suggestion";
import { ReactRenderer } from "@tiptap/react";

import { DEFAULT_EXAMPLE_SKILL_DESCRIPTION, DEFAULT_EXAMPLE_SKILL_NAME } from "../constants";

import ContextMenu, { type ContextMenuHandle } from "@/components/ContentArea/ContextMenu";
import { Commands, type SuggestionItem } from "@/components/ContentArea/ContextMenu/utils";

const FRONTMATTER_PLACEHOLDER = `name: ${DEFAULT_EXAMPLE_SKILL_NAME}
description: >
  ${DEFAULT_EXAMPLE_SKILL_DESCRIPTION}
compatibility: Optional requirements or dependencies`;

interface GetSkillMarkdownSuggestionItemsParams {
  query: string;
  onInsertFrontmatter?: ({ editor, range }: { editor: Editor; range: Range }) => void;
}

const getSkillMarkdownSuggestionItems = ({
  query,
  onInsertFrontmatter,
}: GetSkillMarkdownSuggestionItemsParams): SuggestionItem[] => {
  const items: SuggestionItem[] = [
    {
      title: "Frontmatter",
      description: "Insert a frontmatter placeholder",
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        if (onInsertFrontmatter) {
          onInsertFrontmatter({ editor, range });
          return;
        }

        editor.chain().focus().deleteRange(range).run();
      },
    },
    {
      title: "Paragraph",
      description: "Insert a normal paragraph",
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        editor.chain().focus().deleteRange(range).setParagraph().run();
      },
    },
    {
      title: "Heading 1",
      description: "Large section heading",
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        editor.chain().focus().deleteRange(range).toggleHeading({ level: 1 }).run();
      },
    },
    {
      title: "Heading 2",
      description: "Medium section heading",
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        editor.chain().focus().deleteRange(range).toggleHeading({ level: 2 }).run();
      },
    },
    {
      title: "Heading 3",
      description: "Small section heading",
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        editor.chain().focus().deleteRange(range).toggleHeading({ level: 3 }).run();
      },
    },
    {
      title: "Bullet List",
      description: "Create a bulleted list",
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        editor.chain().focus().deleteRange(range).toggleBulletList().run();
      },
    },
    {
      title: "Numbered List",
      description: "Create an ordered list",
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        editor.chain().focus().deleteRange(range).toggleOrderedList().run();
      },
    },
    {
      title: "Quote",
      description: "Insert a blockquote",
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        editor.chain().focus().deleteRange(range).toggleBlockquote().run();
      },
    },
    {
      title: "Code Block",
      description: "Insert a fenced code block",
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        editor.chain().focus().deleteRange(range).setCodeBlock().run();
      },
    },
    {
      title: "Table",
      description: "Insert a 2-column table with headers",
      command: ({ editor, range }: { editor: Editor; range: Range }) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertTable({ rows: 2, cols: 2, withHeaderRow: true })
          .run();
      },
    },
  ];

  return items.filter(
    item =>
      item.title.toLowerCase().includes(query.toLowerCase()) ||
      (item.description?.toLowerCase().includes(query.toLowerCase()) ?? false),
  );
};

export const getSkillMarkdownFrontmatterPlaceholder = () => FRONTMATTER_PLACEHOLDER;

export const getSkillMarkdownSlashCommands = (params?: {
  onInsertFrontmatter?: ({ editor, range }: { editor: Editor; range: Range }) => void;
}) =>
  Commands.configure({
    suggestion: {
      char: "/",
      items: ({ query }: { query: string }) =>
        getSkillMarkdownSuggestionItems({
          query,
          onInsertFrontmatter: params?.onInsertFrontmatter,
        }),
      render: () => {
        let component: ReactRenderer<ContextMenuHandle>;
        let popup: { element: HTMLElement } | undefined;
        let blurHandler: (() => void) | undefined;

        const destroyPopup = () => {
          if (popup?.element?.parentNode) {
            popup.element.parentNode.removeChild(popup.element);
          }
          popup = undefined;
        };

        return {
          onStart: (props: SuggestionProps) => {
            component = new ReactRenderer(ContextMenu, {
              props,
              editor: props.editor,
            });

            if (!props.clientRect) {
              return;
            }

            const element = component.element as HTMLElement;
            element.style.position = "absolute";
            element.style.zIndex = "9999";
            element.setAttribute("data-commands-menu", "true");

            if (props.items.length === 0) {
              element.style.display = "none";
            }

            document.body.appendChild(element);

            const rect = props.clientRect?.();
            if (!rect) {
              return;
            }

            computePosition({ getBoundingClientRect: () => rect }, element, {
              placement: "bottom-start",
              middleware: [offset(6), flip(), shift({ padding: 8 })],
            }).then(({ x, y }) => {
              element.style.left = `${x}px`;
              element.style.top = `${y}px`;
            });

            popup = { element };

            blurHandler = () => {
              destroyPopup();
            };
            props.editor.on("blur", blurHandler);
          },

          onUpdate(props: SuggestionProps) {
            if (!props.editor.isFocused) {
              destroyPopup();
              return;
            }

            component.updateProps(props);

            if (popup?.element) {
              popup.element.style.display = props.items.length === 0 ? "none" : "";
            }

            const rect = props.clientRect?.();
            if (!rect || !popup?.element) {
              return;
            }

            const popupEl = popup.element;
            computePosition({ getBoundingClientRect: () => rect }, popupEl, {
              placement: "bottom-start",
              middleware: [offset(6), flip(), shift({ padding: 8 })],
            }).then(({ x, y }) => {
              popupEl.style.left = `${x}px`;
              popupEl.style.top = `${y}px`;
            });
          },

          onKeyDown({ event }: { event: KeyboardEvent }) {
            if (event.key === "Escape") {
              destroyPopup();
              return true;
            }

            return component?.ref?.onKeyDown(event) ?? false;
          },

          onExit() {
            destroyPopup();
            if (blurHandler) {
              component.editor.off("blur", blurHandler);
              blurHandler = undefined;
            }
            component?.destroy();
          },
        };
      },
    },
  });
